// This module can be bundled into a package for better reusability.
const axios = require('axios');
const { getEnvironmentFromUrl } = require('@nuskin/ns-common-lib');
const config = require('./environment');

// contentstack HTTP service
const env = getEnvironmentFromUrl(window.location.host);
const envConfig = config.getCredentials(env);
const baseURL = `${envConfig.url}/stacks/${envConfig.apiKey}?environment=${envConfig.env}`;
const headers = {
    'access_token': `${envConfig.accessToken}`,
    'Content-Type': 'application/json'
};
const http = axios.create({ baseURL, headers });

// in-memory caching
const cacheKeys = { aemConfig: 'aemConfig', equinoxMarkets: 'equinoxMarkets' };
const cacheTTLInSeconds = 10 * 1000; // 10 seconds
const cache = {
    [cacheKeys.aemConfig]: { data: null, ttl: Date.now() + cacheTTLInSeconds },
    [cacheKeys.equinoxMarkets]: { data: null, ttl: Date.now() + cacheTTLInSeconds }
};

/**
 * isCacheExpired checks if the cached data is expired.
 *
 * @param {string} key
 * @returns {boolean}
 */
function isCacheExpired(key) {
    return cache[key].data === null || Date.now() > cache[key].ttl;
}

/**
 * setCachedData will replace the cached data and update the TTL.
 *
 * @param {string} key
 * @param {MarketConfig|EquinoxEnabledMarkets} data
 * @returns {void}
 */
function setCachedData(key, data) {
    cache[key].data = data;
    cache[key].ttl = Date.now() + cacheTTLInSeconds;
}

/**
 * getCachedData returns the cached data.
 *
 * @param {string} key
 */
function getCachedData(key) {
    return cache[key].data;
}

/**
 * getAEMConfig fetches the market config from contentstack.
 *
 * @typedef MarketConfig
 * @property {string} aws_url
 * @property {string} checkout_client_id
 * @property {string} checkout_client_secret
 *
 * @export
 * @return {Promise<null|MarketConfig>} 
 */
async function getAEMConfig() {
    try {
        if (isCacheExpired(cacheKeys.aemConfig)) {
            const marketConfig = `market_config_${env}`;
            const variables = { uid: 'blt52b0b83a89763791' };
            const query = `query AemConfigByUid($uid: String!) {
                aem_config(uid: $uid) {
                    title
                    ${marketConfig} {
                        checkout_client_secret
                        checkout_client_id
                        aws_url
                    }
                }
            }`;

            const { data } = await contentstackService({
                data: { variables, query }
            });

            setCachedData(cacheKeys.aemConfig, data.aem_config[marketConfig]);
        }

        return getCachedData(cacheKeys.aemConfig);
    } catch (error) {
        console.error(error);
        return null;
    }
}

/**
 * getEquinoxMarkets will get the list of Equinox-enabled markets from contentstack
 *
 * @typedef EquinoxEnabledMarket
 * @property {string} market_name
 * @property {string} country_code
 * @property {string} store_id
 * 
 * @typedef EquinoxEnabledMarkets
 * @property {EquinoxEnabledMarket[]} [dev_markets]
 * @property {EquinoxEnabledMarket[]} [test_markets]
 * @property {EquinoxEnabledMarket[]} [staging_markets]
 * @property {EquinoxEnabledMarket[]} [prod_markets]
 *
 * @export
 * @return {Promise<EquinoxEnabledMarkets>} 
 */
async function getEquinoxMarkets() {
    try {
        if (isCacheExpired(cacheKeys.equinoxMarkets)) {
            const marketConfig = `${env}_markets`;
            const variables = { uid: 'blt5633ff9e560f14ef' };
            const query = `query EquinoxMarketsByUid($uid: String!) {
                equinox_markets(uid: $uid) {
                    ${marketConfig} {
                        market_name
                        country_code
                        store_id
                    }
                }
            }`;

            const { data } = await contentstackService({
                data: { variables, query }
            });

            setCachedData(cacheKeys.equinoxMarkets, data.equinox_markets[marketConfig]);
        }

        return getCachedData(cacheKeys.equinoxMarkets);
    } catch (error) {
        console.error(error);
        return [];
    }
}

/**
 * isMarketEnabled checks if the given market is in the list
 * of equinox-enabled markets. It will return the market if found.
 * Otherwise, it returns null.
 *
 * @param {string} market
 * @returns {EquinoxEnabledMarket|null}
 */
async function isMarketEnabled(market) {
    const markets = await getEquinoxMarkets();
    const currentMarket = markets.filter(m => m.country_code.toLowerCase() === market.toLowerCase());

    if (currentMarket.length > 0) {
        return currentMarket[0];
    }

    return null;
}

/**
 * Contentstack HTTP - axios wrapper for contentstack request
 *
 * @param {Object} config  - required param
 * @return {Promise<import('axios').AxiosResponse>}  Promise that resolves to market_list_eq object
 */
async function contentstackService(config) {
    const data = JSON.parse(JSON.stringify(config.data));

    try {
        const response = await http.post('', data);
        return response.data
    } catch (error) {
        console.log(error);
        console.error(error.response.data.errors);
    }
}

module.exports = {
    getAEMConfig,
    getEquinoxMarkets,
    isMarketEnabled
}